import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import StandardExpenseForm from './StandardExpenseForm'; // For adding/editing individual expenses
import QuickEntryForm from './QuickEntryForm'; // For quick multi-entry
import ExpensesList from './ExpensesList';
import ExpenseReports from './ExpenseReports';
import { Card, CardContent } from "../ui/card";
import { Alert, AlertDescription } from '../ui/alert';
import { AlertCircle } from 'lucide-react'; 
import { Button } from "../ui/button";
import { getAuth } from 'firebase/auth';
import { expenseService } from '../../services/expenseService';
import { LoadingSpinner } from '../ui/loading-spinner';
import { Toaster } from "../ui/toaster";
import { useToast } from "../../hooks/use-toast";
// Import Firestore functions and db instance
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { getCurrentTaxYear, formatTaxYear, getTaxYearBoundaries } from '../../helpers/taxYearUtil';
import HelpIcon from '../ui/HelpIcon'; // Import HelpIcon
import { PlusIcon, FileText, PieChart } from 'lucide-react';

// Define the expense categories array with user-friendly labels
const EXPENSE_CATEGORIES = [
  { value: 'travel', label: 'Travel & Mileage' }, // Keep as is, seems clear
  { value: 'parking', label: 'Parking Fees' }, // More specific
  { value: 'subsistence', label: 'Food & Drink (Subsistence)' }, // Clearer
  { value: 'equipment', label: 'Equipment & Instruments' }, // More specific
  { value: 'materials', label: 'Clinical Materials & Consumables' }, // More specific
  { value: 'course', label: 'Courses & CPD Training' }, // Clearer
  { value: 'insurance', label: 'Indemnity & Insurance' }, // More specific
  { value: 'membership', label: 'GDC Fees & Memberships' }, // More specific
  { value: 'telephone', label: 'Phone & Internet' }, // Simpler
  { value: 'software', label: 'Software & Subscriptions' }, // Keep as is, seems clear
  { value: 'other', label: 'Other Business Expenses' }
 ] // Clearer

const ExpensesPage = () => {
  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [mainTab, setMainTab] = useState('add');
  const [taxYearOptions, setTaxYearOptions] = useState([]);
  const [selectedTaxYear, setSelectedTaxYear] = useState('');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [filterType, setFilterType] = useState('yearly');
  const [entryTab, setEntryTab] = useState('quick');
  const [error, setError] = useState(null);
  const auth = getAuth();
  const { toast } = useToast();
  // Add state for currentUser data
  const [currentUserData, setCurrentUserData] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [showStandardForm, setShowStandardForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showQuickEntry, setShowQuickEntry] = useState(false); // Add this missing state

  // Fetch currentUser data including professionalRegistrations
  useEffect(() => {
    if (auth.currentUser) {
      setLoadingUser(true);
      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
        if (docSnap.exists()) {
          setCurrentUserData(docSnap.data());
        } else {
          console.log("User document not found in ExpensesPage");
          setCurrentUserData(null); // Handle case where user doc might not exist
        }
        setLoadingUser(false);
      }, (err) => {
        console.error("Error fetching user data in ExpensesPage:", err);
        setError("Could not load user profile data.");
        setLoadingUser(false);
      });

      return () => unsubscribe(); // Cleanup listener on unmount
    } else {
      setLoadingUser(false); // No user, not loading
      setCurrentUserData(null);
    }
  }, [auth.currentUser]);


  useEffect(() => {
    if (auth.currentUser) {
      // Only load expenses immediately if NOT on the 'add' tab
      if (mainTab !== 'add') {
        loadExpenses();
      }
    }
  }, [auth.currentUser, mainTab]);

  useEffect(() => {
    if (expenses.length > 0) {
      const currentYear = getCurrentTaxYear();
      const years = [];
      
      years.push({
        value: currentYear.toString(),
        label: formatTaxYear(currentYear)
      });
      
      years.push({
        value: (currentYear - 1).toString(),
        label: formatTaxYear(currentYear - 1)
      });
      
      if (filterType === 'yearly') {
        if (expenses.some(expense => {
          let expenseDate = null;
          if (expense.date) {
            if (typeof expense.date.toDate === 'function') {
              expenseDate = expense.date.toDate();
            } else if (expense.date._seconds !== undefined) {
              expenseDate = new Date(expense.date._seconds * 1000);
            } else {
              expenseDate = new Date(expense.date);
            }
          } else if (expense.expenseDate) {
            expenseDate = new Date(expense.expenseDate);
          }
          
          if (expenseDate && !isNaN(expenseDate.getTime())) {
            const expenseYear = expenseDate.getFullYear();
            const expenseMonth = expenseDate.getMonth();
            const expenseDay = expenseDate.getDate();
            
            const expenseTaxYear = (expenseMonth < 3 || (expenseMonth === 3 && expenseDay < 6))
              ? expenseYear - 1
              : expenseYear;
              
            return expenseTaxYear < currentYear - 1;
          }
          return false;
        })) {
          years.push({
            value: (currentYear - 2).toString(),
            label: formatTaxYear(currentYear - 2)
          });
        }
      }
      
      setTaxYearOptions(years);
      
      if (!selectedTaxYear) {
        setSelectedTaxYear(currentYear.toString());
      }
    }
  }, [expenses, selectedTaxYear, filterType]);

  useEffect(() => {
    if (selectedTaxYear) {
      const year = parseInt(selectedTaxYear);
      const { start, end } = getTaxYearBoundaries(year);
      
      setDateRange({
        from: start,
        to: end
      });
    }
  }, [selectedTaxYear]);

  const loadExpenses = async () => {
    if (!auth.currentUser) return;
    
    // Check if we already have data and it's not stale
    if (expenses.length > 0 && !isDataStale()) {
      return; // Use existing data if it's fresh
    }
    
    setLoading(true);
    try {
      let loadedExpenses = [];
      
      try {
        // Calculate date 24 months ago from today
        const twoYearsAgo = new Date();
        twoYearsAgo.setMonth(twoYearsAgo.getMonth() - 24);
        
        // Ensure we include at least the current and previous tax year
        const currentTaxYear = getCurrentTaxYear();
        const startOfPreviousTaxYear = getTaxYearBoundaries(currentTaxYear - 1).start;
        
        // Use the earlier of 24 months ago or start of previous tax year
        const cutoffDate = twoYearsAgo < startOfPreviousTaxYear ? 
          twoYearsAgo.toISOString() : 
          startOfPreviousTaxYear.toISOString();
        
        // Pass the cutoff date to the fetchExpenses function
        loadedExpenses = await expenseService.fetchExpenses(auth.currentUser.uid, cutoffDate);
        
        localStorage.setItem('cachedExpenses', JSON.stringify(loadedExpenses));
        localStorage.setItem('lastExpensesSync', new Date().toISOString());
      } catch (error) {
        console.warn('Failed to fetch expenses from Firebase, using cached data:', error);
        const cachedData = localStorage.getItem('cachedExpenses');
        if (cachedData) {
          loadedExpenses = JSON.parse(cachedData);
        }
      }
      
      setExpenses(loadedExpenses);
    } catch (error) {
      console.error('Error loading expenses:', error);
      setError('Failed to load expenses. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Check if data is stale (older than 15 minutes)
  const isDataStale = () => {
    const lastSync = localStorage.getItem('lastExpensesSync');
    if (!lastSync) return true;
    
    const fifteenMinutesAgo = new Date();
    fifteenMinutesAgo.setMinutes(fifteenMinutesAgo.getMinutes() - 15);
    
    return new Date(lastSync) < fifteenMinutesAgo;
  };

  const handleExpenseSaved = (savedExpenses) => {
    if (Array.isArray(savedExpenses)) {
      toast({
        title: "Expenses Saved",
        description: `Successfully saved ${savedExpenses.length} expenses`,
        variant: "success",
      });
    } else {
      toast({
        title: "Expense Saved",
        description: "Your expense was saved successfully",
        variant: "success",
      });
    }
    
    loadExpenses();
  };

  const handleFilterTypeChange = (type) => {
    setFilterType(type);
    const currentTaxYear = getCurrentTaxYear();
    setSelectedTaxYear(currentTaxYear.toString());
  };

  const handleTabChange = (value) => {
    setMainTab(value);
    
    // Load expenses when switching to a tab that needs the data
    if ((value === 'history' || value === 'reports') && 
       (expenses.length === 0 || isDataStale())) {
      loadExpenses();
    }
  };

  // Function to manually update expenses array with archived data
  const updateExpensesWithArchive = (archivedExpenses) => {
    // Merge with current expenses
    const updatedExpenses = [...expenses];
    
    // Add archived expenses that aren't already in the array
    archivedExpenses.forEach(archived => {
      if (!updatedExpenses.some(current => current.id === archived.id)) {
        updatedExpenses.push(archived);
      }
    });
    
    setExpenses(updatedExpenses);
  };
  
  // Force reload expenses data from Firestore
  const forceRefreshExpenses = async () => {
    // Clear existing data
    setExpenses([]);
    // Force reload from Firestore
    await loadExpenses();
  };

  // Update the expenses array without a full refresh
  const updateExpensesArray = (updatedExpenses) => {
    setExpenses(updatedExpenses);
  };
  
  const handleSaveExpense = () => {
    setShowStandardForm(false);
    setEditingExpense(null);
    refreshExpenses(); // Refresh the list after saving
  };

  const handleCancelEdit = () => {
    setShowStandardForm(false);
    setEditingExpense(null);
  };
  
  // **Define refreshExpenses here, before it's used in useEffect or other handlers**
  const refreshExpenses = useCallback(async (options = {}) => {
    // If options.action is 'update' and options.data is provided,
    // update the state directly without fetching. This is useful for optimistic updates.
    if (options.action === 'update' && options.data) {
      setExpenses(options.data);
      // Potentially update other derived states if necessary
      return;
    }

    if (!auth.currentUser) {
      setExpenses([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null); // Clear previous errors
    try {
      const fetchedExpenses = await expenseService.getExpenses(auth.currentUser.uid);
      setExpenses(fetchedExpenses);
    } catch (err) {
      console.error("Error fetching expenses:", err);
      setError("Failed to load expenses. Please try again.");
      setExpenses([]); // Clear expenses on error
      toast({
        title: "Error",
        description: "Failed to load expenses.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [auth.currentUser, toast]); // Added toast to dependencies as it's used

  useEffect(() => {
    if (auth.currentUser) {
      refreshExpenses();
    } else {
      // Clear data if user logs out or is not available
      setExpenses([]);
      setLoading(false);
    }
  }, [auth.currentUser, refreshExpenses]); // refreshExpenses is a dependency

  const handleAddNewExpense = () => {
    setEditingExpense(null);
    setShowStandardForm(true);
    setShowQuickEntry(false); // Hide quick entry if opening standard form
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setShowStandardForm(true);
    setShowQuickEntry(false); // Hide quick entry
  };

  const handleQuickEntrySave = (savedQuickExpenses) => {
    // Optionally, you could merge savedQuickExpenses into the current expenses list
    // for a more immediate UI update, or just rely on refreshExpenses.
    refreshExpenses(); // Refresh the main list
    // setShowQuickEntry(false); // Optionally close quick entry form
    toast({
      title: "Quick Expenses Saved",
      description: `${savedQuickExpenses.length} expense(s) added.`,
      variant: "success",
    });
  };
  
  return (
    <div className="container mx-auto p-4 max-w-5xl">
      {/* Header with title and navigation buttons */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        {/* Add HelpIcon next to the title */}
        <div className="flex items-center gap-2">
          {/* Change text-white to text-accent-foreground */}
          <h1 className="text-xl sm:text-2xl font-bold text-accent-foreground">Expenses Management</h1>
          <HelpIcon sectionKey="ExpensesPage" /> {/* Use a relevant sectionKey */}
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          {/* Add Expenses Button */}
          <Button
            variant={mainTab === 'add' ? 'default' : 'outline'} // Use variant prop
            className={`
              flex items-center text-xs space-x-1 transition-colors duration-150 ease-in-out
              ${mainTab === 'add'
                ? 'app-accent-purple-bg text-white border-transparent' // Active state
                : 'border-border text-gray-700 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-gray-100' // Inactive outline state with explicit hover text
              }
            `}
            onClick={() => handleTabChange('add')}
          >
            <PlusIcon className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Add Expenses</span>
          </Button>
          {/* History Button */}
          <Button
            variant={mainTab === 'history' ? 'default' : 'outline'} // Use variant prop
            className={`
              flex items-center text-xs space-x-1 transition-colors duration-150 ease-in-out
              ${mainTab === 'history'
                ? 'app-accent-purple-bg text-white border-transparent' // Active state
                : 'border-border text-gray-700 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-gray-100' // Inactive outline state with explicit hover text
              }
            `}
            onClick={() => handleTabChange('history')}
          >
            <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>History</span>
          </Button>
          {/* Reports Button */}
          <Button
            variant={mainTab === 'reports' ? 'default' : 'outline'} // Use variant prop
            className={`
              flex items-center text-xs space-x-1 transition-colors duration-150 ease-in-out
              ${mainTab === 'reports'
                ? 'app-accent-purple-bg text-white border-transparent' // Active state
                : 'border-border text-gray-700 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-gray-100' // Inactive outline state with explicit hover text
              }
            `}
            onClick={() => handleTabChange('reports')}
          >
            <PieChart className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Reports</span>
          </Button>
        </div>
      </div>

      {/* Main content area */}
      {mainTab === 'add' ? (
        // Apply theme classes to the container Card/div
        <div className="app-card-bg rounded-lg shadow-lg border border-border overflow-hidden p-3 sm:p-4">
          {loadingUser ? (
             <div className="flex justify-center items-center h-40">
               <LoadingSpinner />
             </div>
          ) : (
            <Tabs value={entryTab} onValueChange={setEntryTab}>
              {/* Apply theme classes to TabsList */}
              <TabsList className="grid grid-cols-2 mb-4 app-bg-secondary border-border">
                {/* Apply theme classes to TabsTrigger */}
                <TabsTrigger 
                  value="quick" 
                  className="text-xs sm:text-sm py-1.5 sm:py-2 text-muted-foreground data-[state=active]:app-accent-purple-bg data-[state=active]:!text-white"
                >
                  Quick Entry
                </TabsTrigger>
                <TabsTrigger 
                  value="standard" 
                  className="text-xs sm:text-sm py-1.5 sm:py-2 text-muted-foreground data-[state=active]:app-accent-purple-bg data-[state=active]:!text-white"
                >
                  Standard
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="quick">
                <QuickEntryForm onExpenseSaved={handleExpenseSaved} />
              </TabsContent>
              
              <TabsContent value="standard">
                <StandardExpenseForm
                  onExpenseSaved={handleExpenseSaved}
                  categories={EXPENSE_CATEGORIES}
                  // Pass professionalRegistrations down
                  professionalRegistrations={currentUserData?.professionalRegistrations}
                  userId={auth.currentUser?.uid} // Pass userId for profile update
                  gdcExpiryDate={currentUserData?.gdcExpiryDate}
                  gdcProvider={currentUserData?.gdcProvider}
                  insuranceExpiryDate={currentUserData?.insuranceExpiryDate}
                  insuranceProvider={currentUserData?.insuranceProvider}
                />
              </TabsContent>
            </Tabs>
          )}
        </div>
      ) : mainTab === 'history' ? (
        // Apply theme classes to the container Card
        <Card className="app-card-bg border-border shadow-lg overflow-x-auto">
          <CardContent className="p-3 sm:p-4">
            <ExpensesList 
              expenses={expenses}
              refreshExpenses={(param) => {
                if (Array.isArray(param)) {
                  // Archive data case - just merge the arrays
                  updateExpensesWithArchive(param);
                } else if (param && param.action === 'update' && param.data) {
                  // Direct update case - use the provided array
                  updateExpensesArray(param.data);
                } else {
                  // Normal refresh - don't clear first
                  loadExpenses();
                }
              }}
            />
          </CardContent>
        </Card>
      ) : ( // mainTab === 'reports'
        // Apply theme classes to the container Card
        <Card className="app-card-bg border-border shadow-lg overflow-x-auto">
          <CardContent className="p-3 sm:p-4">
            <ExpenseReports 
              expenses={expenses} 
              filterType={filterType}
              onFilterTypeChange={handleFilterTypeChange}
              onExpensesUpdate={updateExpensesWithArchive}
            />
          </CardContent>
        </Card>
      )}
      
      {/* Error Alert */}
      {error && (
        // Apply theme classes to Alert
        <Alert variant="destructive" className="mb-4 mt-4 text-xs sm:text-sm bg-red-500/10 border-red-500/50 text-red-600 dark:text-red-400">
          <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <Toaster />
    </div>
  );
};

export default ExpensesPage;
